// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { findStep } from "@werner/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext, withStepperContext } from "@werner/App/Widgets/CheckoutStepper/StepperContext";
import Check from "@werner/XDL/Icons/Success1";
import { fontConfig } from "@werner/XDL/PreStyle/typography";
import { color, layoutContainer, xScreen } from "@werner/XDL/Utilities";
import React, { useContext } from "react";
import { css } from "styled-components";

interface OwnProps {}

type Props = OwnProps;

export interface CheckoutStepperStyles {
    container?: InjectableCss;
    stepperTitle?: TypographyProps;
    stepperWrapper?: InjectableCss;
    stepperItem: {
        wrapper?: InjectableCss;
        insideWrapper?: InjectableCss;
        stepNumber?: InjectableCss;
        stepIcon?: IconPresentationProps;
        title?: TypographyProps;
    };
}
const sharedStepHoverStyles = css`
    color: ${color("primary")};
    &:first-child {
        border: 2px solid ${color("primary")};
    }
`;
export const styles: CheckoutStepperStyles = {
    container: {
        css: css`
            width: 100%;
            border: 1px solid ${color("primary", 0.05)};
            background-color: ${color("common.white")};
            box-shadow: 0 0 15px 0 ${color("primary", 0.15)};
            margin-bottom: 80px;
            & > .inner-container {
                padding-top: 36px;
                padding-bottom: 36px;
                ${layoutContainer}
                display: flex;
                align-items: center;
                column-gap: 60px;
                row-gap: 20px;
                ${xScreen("lte", 575)} {
                    flex-wrap: wrap;
                }
            }
            ${xScreen("lte", "sm")} {
                margin-bottom: 34px;
            }
        `,
    },
    stepperWrapper: {
        css: css`
            display: flex;
            width: 100%;
            align-items: flex-start;
            max-width: 812px;
            justify-content: space-between;
        `,
    },
    stepperTitle: {
        variant: "h1",
    },
    stepperItem: {
        wrapper: {
            css: css`
                display: flex;
                justify-content: flex-start;
                position: relative;

                &.clickable {
                    cursor: pointer;
                    & > .inside-wrapper {
                        &:hover {
                            & > ${TypographyStyle} {
                                ${({ isActive }: any) => {
                                    if (isActive) {
                                        return css`
                                            background: ${color("common.white")};
                                            ${sharedStepHoverStyles};
                                        `;
                                    }
                                    return sharedStepHoverStyles;
                                }}
                            }
                        }
                    }
                }
            `,
        },
        insideWrapper: {
            css: css`
                display: flex;
                z-index: 10;
                align-items: center;
                flex-direction: row;
                ${xScreen("lte", "sm")} {
                    flex-direction: column;
                }
            `,
        },
        stepNumber: {
            css: css`
                display: flex;
                align-items: center;
                justify-content: center;
                width: 40px;
                height: 40px;
                border-radius: 50%;
                background: ${color("common.white")};
                font-size: 16px;
                letter-spacing: 0;
                line-height: 18px;
                font-weight: bold;
                font-family: ${fontConfig.fontFamily};
                color: ${color("common.ui3", 0.3)};
                border: 2px solid ${color("common.ui3", 0.3)};
                ${({ isCompleted, isActive }: any) => {
                    if (isActive) {
                        return css`
                            background: ${color("primary")};
                            color: ${color("common.white")};
                            border: 2px solid ${color("primary")};
                        `;
                    }
                    if (isCompleted) {
                        return css`
                            color: ${color("primary")};
                            border: 2px solid ${color("primary")};
                        `;
                    }
                }};
            `,
        },
        stepIcon: {
            src: Check,
            size: 16,
            color: "primary",
        },
        title: {
            size: 16,
            weight: 500,
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                color: ${color("common.ui3", 0.3)};
                margin-left: 8px;
                ${({ isCompleted, isActive }: any) => {
                    if (isCompleted || isActive) {
                        return css`
                            color: ${color("primary")};
                        `;
                    }
                }};
            `,
        },
    },
};

export const CheckoutStepper: React.FC<Props> = () => {
    const { steps, currentStep, maxStep, moveToStepHandler } = useContext(StepperContext);
    const currentStepText = findStep(steps, currentStep).name;

    const StepperComponent = () => {
        const style = styles.stepperItem;
        return (
            <Typography as="ul" {...styles.stepperWrapper}>
                {steps.map((item, index) => {
                    const isCompleted = item.index < currentStep;
                    const isActive = item.index === currentStep;
                    const isClickable = item.index <= maxStep;

                    const props = { isCompleted, isActive, isClickable };
                    const className = `${isActive ? "active" : ""} ${isCompleted ? "completed" : ""} ${
                        isClickable ? "clickable" : ""
                    }`.trim();

                    return (
                        <Typography
                            as="li"
                            {...style.wrapper}
                            {...props}
                            key={item.name}
                            className={className}
                            onClick={() => isClickable && moveToStepHandler(item.index)}
                            role="link"
                            aria-label="Move to Step Clickable"
                        >
                            <StyledWrapper {...style.insideWrapper} {...props} className="inside-wrapper">
                                <Typography {...style.stepNumber} {...props}>
                                    {isCompleted ? <Icon {...style.stepIcon} /> : index + 1}
                                </Typography>
                                <Typography {...style.title} {...props}>
                                    {item.name}
                                </Typography>
                            </StyledWrapper>
                        </Typography>
                    );
                })}
            </Typography>
        );
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper className="inner-container">
                <Typography {...styles.stepperTitle}>{translate("Checkout")}</Typography>
                <VisuallyHidden aria-current="step">{translate(currentStepText)}</VisuallyHidden>
                <StepperComponent />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withStepperContext(CheckoutStepper),
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Stepper",
        fieldDefinitions: [],
    },
};

export default widgetModule;
